<template>
  <div class="career-detail">
    <div class="mb-6">
      <button
        @click="$router.push('/career')"
        class="flex items-center gap-2 text-neutral-600 hover:text-primary-500 transition-colors mb-4"
      >
        <span>←</span>
        <span>Back to Career List</span>
      </button>

      <div class="bg-white rounded-lg shadow-sm p-6">
        <h2 class="text-2xl font-bold text-neutral-800 mb-4">
          {{ careerData.title }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <p class="text-sm text-neutral-500">Location</p>
            <p class="font-medium text-neutral-700">
              {{ careerData.location }}
            </p>
          </div>
          <div>
            <p class="text-sm text-neutral-500">Work Type</p>
            <p class="font-medium">
              <span
                class="px-2 py-1 rounded text-xs font-medium"
                :class="workTypeClass(careerData.work_type)"
              >
                {{ careerData.work_type }}
              </span>
            </p>
          </div>
          <div>
            <p class="text-sm text-neutral-500">Period</p>
            <p class="font-medium text-neutral-700">
              {{ formatDate(careerData.start_date) }} -
              {{ formatDate(careerData.end_date) }}
            </p>
          </div>
        </div>
        <div>
          <p class="text-sm text-neutral-500 mb-2">Description</p>
          <div class="text-neutral-700" v-html="careerData.description"></div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow-sm p-6">
      <div class="flex gap-5 justify-between items-end mb-6 mdmax:flex-col">
        <h3 class="text-xl font-bold text-neutral-800">Job Applications</h3>
        <div class="flex gap-3 mdmax:w-full">
          <div class="w-48 mdmax:w-full">
            <CustomSelect
              :options="statusOptions"
              v-model="statusFilter"
              :default="statusFilter"
              placeholder="All Status"
              @change="onFilterChange"
            />
          </div>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr
              class="bg-neutral-50 text-neutral-500 text-sm font-semibold border-b border-neutral-200"
            >
              <th class="p-4">No</th>
              <th class="p-4">Name</th>
              <th class="p-4">Email</th>
              <th class="p-4">Phone</th>
              <th class="p-4">Status</th>
              <th class="p-4">Applied Date</th>
              <th class="p-4">Action</th>
            </tr>
          </thead>
          <tbody class="text-neutral-700 text-sm">
            <tr
              v-for="(submission, index) in items"
              :key="submission.id"
              class="border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
            >
              <td class="p-4">{{ (page - 1) * limit + index + 1 }}</td>
              <td class="p-4 font-medium">{{ submission.name }}</td>
              <td class="p-4">{{ submission.email }}</td>
              <td class="p-4">{{ submission.phone_number }}</td>
              <td class="p-4">
                <span
                  class="px-2 py-1 rounded text-xs font-medium"
                  :class="statusClass(submission.status)"
                >
                  {{ submission.status }}
                </span>
              </td>
              <td class="p-4">{{ formatDate(submission.created_at) }}</td>
              <td class="p-4">
                <div class="flex items-center gap-2">
                  <button
                    @click="viewSubmission(submission)"
                    class="text-neutral-500 hover:text-primary-500 transition-colors"
                    title="View Details"
                  >
                    <Eye width="18" height="18" />
                  </button>
                  <button
                    @click="openStatusModal(submission)"
                    class="text-neutral-500 hover:text-blue-500 transition-colors"
                    title="Update Status"
                  >
                    <Edit width="18" height="18" />
                  </button>
                  <button
                    @click="onDelete(submission)"
                    class="text-neutral-500 hover:text-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash width="18" height="18" />
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!items || items.length === 0">
              <td colspan="7" class="p-4 text-center text-neutral-500">
                No applications found!
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="text-neutral-500 font-medium mt-4 text-sm"
        v-if="!isMobile && items.length > 0"
      >
        Showing {{ (page - 1) * limit + 1 }} -
        {{ Math.min(page * limit, meta.total_count) }} of {{ meta.total_count }}
      </div>
      <div class="mt-8 flex justify-end items-center" v-if="items.length > 0">
        <pagination
          :currentPage="page"
          :totalCount="meta.total_count"
          :pageSize="limit"
          @onPageChange="changePage"
        />
      </div>
    </div>

    <Modal
      :modalVisible="detailModalVisible"
      @close="closeDetailModal"
      width="700px"
    >
      <div slot="modal-content">
        <h3 class="text-xl font-bold text-neutral-800 mb-4">
          Application Details
        </h3>
        <div v-if="selectedSubmission" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-neutral-500">Name</p>
              <p class="font-medium text-neutral-700">
                {{ selectedSubmission.name }}
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-500">Email</p>
              <p class="font-medium text-neutral-700">
                {{ selectedSubmission.email }}
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-500">Phone Number</p>
              <p class="font-medium text-neutral-700">
                {{ selectedSubmission.phone_number }}
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-500">Status</p>
              <span
                class="px-2 py-1 rounded text-xs font-medium inline-block"
                :class="statusClass(selectedSubmission.status)"
              >
                {{ selectedSubmission.status }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-sm text-neutral-500 mb-1">CV</p>
            <a
              :href="selectedSubmission.cv_url"
              target="_blank"
              class="text-primary-500 hover:text-primary-600 underline"
            >
              View CV
            </a>
          </div>
          <div v-if="selectedSubmission.portfolio_url">
            <p class="text-sm text-neutral-500 mb-1">Portfolio</p>
            <a
              :href="selectedSubmission.portfolio_url"
              target="_blank"
              class="text-primary-500 hover:text-primary-600 underline"
            >
              View Portfolio
            </a>
          </div>
          <div>
            <p class="text-sm text-neutral-500 mb-1">Reason to Hire</p>
            <p class="text-neutral-700">
              {{ selectedSubmission.reason_to_hire }}
            </p>
          </div>
          <div>
            <p class="text-sm text-neutral-500 mb-1">Candidate Description</p>
            <p class="text-neutral-700">
              {{ selectedSubmission.candidate_description }}
            </p>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-neutral-500">Applied Date</p>
              <p class="font-medium text-neutral-700">
                {{ formatDate(selectedSubmission.created_at) }}
              </p>
            </div>
            <div>
              <p class="text-sm text-neutral-500">Last Updated</p>
              <p class="font-medium text-neutral-700">
                {{ formatDate(selectedSubmission.updated_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Modal>

    <Modal
      :modalVisible="statusModalVisible"
      @close="closeStatusModal"
      width="400px"
    >
      <div slot="modal-content">
        <h3 class="text-lg font-bold text-neutral-800 mb-4">
          Update Application Status
        </h3>
        <div v-if="selectedSubmission" class="mb-6">
          <p class="text-sm text-neutral-600 mb-1">
            Candidate: <strong>{{ selectedSubmission.name }}</strong>
          </p>
          <p class="text-sm text-neutral-600 mb-4">
            Current Status:
            <span
              class="px-2 py-1 rounded text-xs font-medium"
              :class="statusClass(selectedSubmission.status)"
            >
              {{ selectedSubmission.status }}
            </span>
          </p>
          <label class="block text-sm font-semibold mb-2">New Status</label>
          <select
            v-model="newStatus"
            class="w-full border border-neutral-300 rounded px-3 py-2 focus:ring focus:ring-blue-400 focus:border-blue-400"
          >
            <option value="">Select Status</option>
            <option
              v-for="status in statusOptions"
              :key="status"
              :value="status"
            >
              {{ status }}
            </option>
          </select>
        </div>
        <div class="flex justify-end gap-3">
          <button
            @click="closeStatusModal"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
          >
            Cancel
          </button>
          <button
            @click="confirmUpdateStatus"
            class="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 font-medium"
            :disabled="!newStatus"
          >
            Update Status
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      :modalVisible="deleteModalVisible"
      @close="closeDeleteModal"
      width="400px"
    >
      <div slot="modal-content" class="text-center">
        <h3 class="text-lg font-bold text-neutral-800 mb-2">
          Delete Application
        </h3>
        <p class="text-neutral-600 mb-6">
          Are you sure you want to delete the application from
          <strong>{{
            submissionToDelete ? submissionToDelete.name : ""
          }}</strong
          >?
        </p>
        <div class="flex justify-center gap-3">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
          >
            Cancel
          </button>
          <button
            @click="confirmDelete"
            class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
          >
            Delete
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import dayjs from "dayjs"

export default {
  name: "CareerDetail",
  components: {
    CustomSelect: () => import("@/components/Forms/CustomSelect"),
    Pagination: () => import("@/components/Pagination/Pagination"),
    Eye: () => import("@/components/Icons/Eye"),
    Edit: () => import("@/components/Icons/Edit"),
    Trash: () => import("@/components/Icons/Trash"),
    Modal: () => import("@/components/Modal/Modal")
  },
  data: () => ({
    careerId: null,
    statusFilter: "",
    page: 1,
    limit: 10,
    statusOptions: [
      "APPLIED",
      "SHORTLISTED",
      "INTERVIEWING",
      "OFFERED",
      "HIRED",
      "REJECTED"
    ],
    detailModalVisible: false,
    statusModalVisible: false,
    deleteModalVisible: false,
    selectedSubmission: null,
    submissionToDelete: null,
    newStatus: ""
  }),
  computed: {
    ...mapGetters("career", ["career", "submissions"]),
    ...mapGetters("common", ["isMobile"]),
    careerData() {
      return this.career.data || {}
    },
    meta() {
      return this.submissions.data || { total_pages: 1, total_count: 0 }
    },
    items() {
      return this.submissions.data?.items || []
    }
  },
  methods: {
    ...mapActions("career", [
      "getCareer",
      "getSubmissionsList",
      "updateSubmissionStatus",
      "deleteSubmission"
    ]),
    ...mapActions("common", ["showLoading", "hideLoading", "showExactToast"]),

    formatDate(date) {
      if (!date) return "-"
      const rawDate = typeof date === "string" ? date.replace("Z", "") : date
      return dayjs(rawDate).format("DD MMM YYYY")
    },
    workTypeClass(workType) {
      if (workType === "Remote") return "bg-blue-100 text-blue-700"
      if (workType === "Hybrid") return "bg-purple-100 text-purple-700"
      if (workType === "Onsite") return "bg-green-100 text-green-700"
      return "bg-gray-100 text-gray-700"
    },
    statusClass(status) {
      const classes = {
        APPLIED: "bg-blue-100 text-blue-700",
        SHORTLISTED: "bg-purple-100 text-purple-700",
        INTERVIEWING: "bg-yellow-100 text-yellow-700",
        OFFERED: "bg-green-100 text-green-700",
        HIRED: "bg-emerald-100 text-emerald-700",
        REJECTED: "bg-red-100 text-red-700"
      }
      return classes[status] || "bg-gray-100 text-gray-700"
    },
    fetchCareerDetail() {
      this.showLoading()
      this.getCareer({ id: this.careerId })
        .catch((err) => console.error(err))
        .finally(() => this.hideLoading())
    },
    fetchSubmissions() {
      this.showLoading()
      const params = {
        career_id: this.careerId,
        page: this.page,
        limit: this.limit
      }
      if (this.statusFilter) {
        params.status = this.statusFilter
      }
      this.getSubmissionsList(params)
        .catch((err) => console.error(err))
        .finally(() => this.hideLoading())
    },
    onFilterChange() {
      this.page = 1
      this.fetchSubmissions()
    },
    changePage(val) {
      this.page = val
      this.fetchSubmissions()
    },
    viewSubmission(submission) {
      this.selectedSubmission = submission
      this.detailModalVisible = true
    },
    closeDetailModal() {
      this.detailModalVisible = false
      this.selectedSubmission = null
    },
    openStatusModal(submission) {
      this.selectedSubmission = submission
      this.newStatus = submission.status
      this.statusModalVisible = true
    },
    closeStatusModal() {
      this.statusModalVisible = false
      this.selectedSubmission = null
      this.newStatus = ""
    },
    async confirmUpdateStatus() {
      if (!this.selectedSubmission || !this.newStatus) return

      this.showLoading()
      try {
        await this.updateSubmissionStatus({
          id: this.selectedSubmission.id,
          payloads: { status: this.newStatus }
        })
        this.showExactToast({
          message: "Application status updated successfully",
          type: "success"
        })
        this.fetchSubmissions()
      } catch (error) {
        console.error(error)
        this.showExactToast({
          message: error.response?.data?.message || "Failed to update status",
          type: "error"
        })
      } finally {
        this.hideLoading()
        this.closeStatusModal()
      }
    },
    onDelete(submission) {
      this.submissionToDelete = submission
      this.deleteModalVisible = true
    },
    closeDeleteModal() {
      this.deleteModalVisible = false
      this.submissionToDelete = null
    },
    async confirmDelete() {
      if (!this.submissionToDelete) return

      this.showLoading()
      try {
        await this.deleteSubmission(this.submissionToDelete.id)
        this.showExactToast({
          message: "Application deleted successfully",
          type: "success"
        })
        this.fetchSubmissions()
      } catch (error) {
        console.error(error)
        this.showExactToast({
          message:
            error.response?.data?.message || "Failed to delete application",
          type: "error"
        })
      } finally {
        this.hideLoading()
        this.closeDeleteModal()
      }
    }
  },
  mounted() {
    this.careerId = this.$route.params.id
    if (this.careerId) {
      this.fetchCareerDetail()
      this.fetchSubmissions()
    }
  }
}
</script>

<style lang="scss" scoped></style>
